import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { Competition, Season } from '../types/sports';
import CompetitionForm from '../components/competitions/CompetitionForm';
import { Plus, Pencil, Trash2, Pause, Play, LayoutGrid, List, BarChart3 } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Competitions overview page with list and card view
 */
const Competitions: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [competitionsData, seasonsData] = await Promise.all([
        sportsApi.getAllCompetitions(),
        sportsApi.getAllSeasons()
      ]);
      setCompetitions(competitionsData);
      setSeasons(seasonsData);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const handleSaveCompetition = async (data: {
    name: string;
    shortName?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    seasonId: number;
    isActive?: boolean;
  }) => {
    try {
      setFormLoading(true);
      if (editingCompetition) {
        await sportsApi.updateCompetition(editingCompetition.id, data);
        showSuccess('Wettbewerb erfolgreich aktualisiert');
      } else {
        await sportsApi.createCompetition(data);
        showSuccess('Wettbewerb erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingCompetition(null);
      await loadData();
    } catch (err: any) {
      const errorMessage = err.message || (editingCompetition ? 'Fehler beim Aktualisieren des Wettbewerbs' : 'Fehler beim Erstellen des Wettbewerbs');
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditCompetition = (competition: Competition) => {
    setEditingCompetition(competition);
    setShowForm(true);
  };

  const handleDeleteCompetition = async (competition: Competition) => {
    if (!window.confirm(`Möchten Sie den Wettbewerb "${competition.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await sportsApi.deleteCompetition(competition.id);
      showSuccess('Wettbewerb erfolgreich gelöscht');
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Löschen des Wettbewerbs');
    }
  };

  const handleToggleActive = async (competition: Competition) => {
    try {
      await sportsApi.toggleCompetitionActive(competition.id);
      showSuccess(`Wettbewerb ${competition.isActive ? 'deaktiviert' : 'aktiviert'}`);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Ändern des Aktivitätsstatus');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCompetition(null);
  };

  const filteredCompetitions = competitions.filter(competition =>
    competition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (competition.shortName && competition.shortName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (competition.season?.name && competition.season.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (competition.season?.league?.name && competition.season.league.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Lade Wettbewerbe...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={loadData}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="page-badge page-badge-active" style={{ padding: '12px', marginBottom: '12px', borderRadius: '6px' }}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="page-badge page-badge-inactive" style={{ padding: '12px', marginBottom: '12px', borderRadius: '6px' }}>
          {errorMessage}
        </div>
      )}

      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Neu
          </button>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Wettbewerbe durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredCompetitions.length} / {competitions.length} Wettbewerbe</span>
        </div>
      </div>

      {/* Content */}
      {filteredCompetitions.length === 0 ? (
        <div className="page-empty">
          <h3>Keine Wettbewerbe gefunden</h3>
          <p>Versuchen Sie einen anderen Suchbegriff.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredCompetitions.map((competition) => (
            <div key={competition.id} className="page-card">
              <div className="page-card-header">
                <h3 className="page-card-title">{competition.name}</h3>
                <div>
                  {competition.shortName && (
                    <span className="page-badge" style={{ background: '#e5e7eb', color: '#374151', marginRight: '4px' }}>{competition.shortName}</span>
                  )}
                  <span className={`page-badge ${competition.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                    {competition.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <div className="page-card-content">
                {competition.description && <span><strong>Beschreibung:</strong> {competition.description}</span>}
                <span><strong>Saison:</strong> {competition.season?.name || `Saison ID: ${competition.seasonId}`}</span>
                {competition.season?.league && <span><strong>Liga:</strong> {competition.season.league.name}</span>}
                {competition.season?.league?.sport && <span><strong>Sport:</strong> {competition.season.league.sport.name}</span>}
                {competition.season?.league?.country && (
                  <span><strong>Land:</strong> {competition.season.league.country.flag} {competition.season.league.country.name}</span>
                )}
                {competition.startDate && <span><strong>Start:</strong> {new Date(competition.startDate).toLocaleDateString('de-DE')}</span>}
                {competition.endDate && <span><strong>Ende:</strong> {new Date(competition.endDate).toLocaleDateString('de-DE')}</span>}
              </div>
              <div className="page-card-actions">
                <span className="page-card-footer">Erstellt: {new Date(competition.createdAt).toLocaleDateString('de-DE')}</span>
                <button className="page-btn-icon toggle" onClick={() => handleToggleActive(competition)} title={competition.isActive ? "Deaktivieren" : "Aktivieren"}>
                  {competition.isActive ? <Pause size={14} /> : <Play size={14} />}
                </button>
                <button className="page-btn-icon edit" onClick={() => handleEditCompetition(competition)} title="Bearbeiten">
                  <Pencil size={14} />
                </button>
                <button className="page-btn-icon delete" onClick={() => handleDeleteCompetition(competition)} title="Löschen">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Wettbewerb</th>
                <th>Saison</th>
                <th>Liga</th>
                <th>Sport</th>
                <th>Land</th>
                <th>Zeitraum</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompetitions.map((competition) => (
                <tr key={competition.id}>
                  <td>
                    {competition.name}
                    {competition.shortName && <span style={{ color: '#6b7280', fontSize: '0.9rem' }}> ({competition.shortName})</span>}
                  </td>
                  <td>{competition.season?.name || `Saison ID: ${competition.seasonId}`}</td>
                  <td>{competition.season?.league?.name || '-'}</td>
                  <td>{competition.season?.league?.sport?.name || '-'}</td>
                  <td>{competition.season?.league?.country?.flag} {competition.season?.league?.country?.name || '-'}</td>
                  <td>
                    {competition.startDate && competition.endDate ? (
                      `${new Date(competition.startDate).toLocaleDateString('de-DE')} - ${new Date(competition.endDate).toLocaleDateString('de-DE')}`
                    ) : competition.startDate ? (
                      `Ab ${new Date(competition.startDate).toLocaleDateString('de-DE')}`
                    ) : competition.endDate ? (
                      `Bis ${new Date(competition.endDate).toLocaleDateString('de-DE')}`
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`page-badge ${competition.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                      {competition.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(competition.createdAt).toLocaleDateString('de-DE')}</td>
                  <td>
                    <div className="page-table-actions">
                      <button className="page-btn-icon toggle" onClick={() => handleToggleActive(competition)} title={competition.isActive ? "Deaktivieren" : "Aktivieren"}>
                        {competition.isActive ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button className="page-btn-icon edit" onClick={() => handleEditCompetition(competition)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => handleDeleteCompetition(competition)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Competition Form Modal */}
      {showForm && (
        <CompetitionForm
          competition={editingCompetition || undefined}
          seasons={seasons}
          onSave={handleSaveCompetition}
          onCancel={handleCancelForm}
          loading={formLoading}
        />
      )}
    </div>
  );
};

export default Competitions;